import { createApiKey, deleteApiKeyById, fetchAllApiKeys } from '@/services/api-key.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { ApiKey } from '@/types/api-key.type';
import type { PaginatedResponse, PaginationParams, QueryOptions } from '@/types/common.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useApiKeysQuery = (
  params: PaginationParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<ApiKey>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.apiKeys.list(params.page || 1, params.page_size, params.query || ''),
    queryFn: () => fetchAllApiKeys(params),
    ...options,
  });
};

export const useCreateApiKeyMutation = (options?: QueryOptions<string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.apiKeys.mutations.create,
    mutationFn: createApiKey,
    onSuccess: data => {
      notifyMutationSuccessWithOptions(
        options,
        'API key created',
        'A new API key was created successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'API key creation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDeleteApiKeyMutation = (options?: QueryOptions<null>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.apiKeys.mutations.delete,
    mutationFn: deleteApiKeyById,
    onMutate: async apiKeyId => {
      await queryClient.cancelQueries({ queryKey: queryKeys.apiKeys.all });
      const previousData = queryClient.getQueryData<PaginatedResponse<ApiKey>>(
        queryKeys.apiKeys.all
      );

      queryClient.setQueryData(
        queryKeys.apiKeys.all,
        (old: PaginatedResponse<ApiKey> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(apiKey => apiKey.id !== apiKeyId),
          };
        }
      );

      return { previousData };
    },
    onError: (error, _variables, onMutateResult) => {
      if (onMutateResult?.previousData) {
        queryClient.setQueryData(queryKeys.apiKeys.all, onMutateResult.previousData);
      }
      notifyMutationErrorWithOptions(options, 'API key deletion failed', error);
      options?.onError?.(error);
    },
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'API key deleted',
        'The API key was deleted successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      options?.onSuccess?.(null);
    },
  });
};
